import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IGlossaryEntry } from '~/types/glossary'

import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { useCreateGlossaryEntryMutation, useUpdateGlossaryEntryMutation } from '~/queries/glossary'

interface FormValues {
  longDescription: string
  shortDescription: string
  title: string
}

const defaultCreateValues: FormValues = {
  longDescription: '',
  shortDescription: '',
  title: '',
}

const validationSchema = Yup.object().shape({
  longDescription: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  shortDescription: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  title: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  entry?: IGlossaryEntry
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
}

const ManageEntryForm = ({ entry, onClose, onFailure, onSuccess }: FormProps): ReactElement => {
  /** Local state */

  const defaultValues: FormValues = entry
    ? {
        longDescription: entry.long_description,
        shortDescription: entry.short_description,
        title: entry.title,
      }
    : defaultCreateValues

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { mutateAsync: createGlossaryEntry } = useCreateGlossaryEntryMutation()

  const { mutateAsync: updateGlossaryEntry } = useUpdateGlossaryEntryMutation()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    try {
      const commonPayload = {
        long_description: values.longDescription,
        short_description: values.shortDescription,
        title: values.title,
      }
      if (entry) {
        await updateGlossaryEntry({ glossary_item_id: entry.glossary_item_id, ...commonPayload })
      } else {
        await createGlossaryEntry(commonPayload)
      }
      onSuccess()
    } catch {
      onFailure()
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          <TextInput
            {...register('title')}
            autoFocus
            error={errors.title?.message}
            label="Word"
            placeholder="Enter a word"
            required
          />
          <TextInput
            {...register('shortDescription')}
            error={errors.shortDescription?.message}
            label="Introduction"
            placeholder="Enter a short description"
            required
          />
          <TextArea
            {...register('longDescription')}
            error={errors.longDescription?.message}
            label="Definition"
            placeholder="Enter a longer description"
            required
          />
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const ManageEntryModal = ({ entry, isOpen, onClose, onFailure, onSuccess }: ModalProps): ReactElement => {
  return (
    <Modal className="manage-entry-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{`${entry ? 'Edit' : 'Create'} an entry`}</ModalHeader>
      <ManageEntryForm entry={entry} onClose={onClose} onFailure={onFailure} onSuccess={onSuccess} />
    </Modal>
  )
}

export default ManageEntryModal
